/** Descriptive public URLs; reference must already be persisted by the server. */
export interface BoatUrlInput {
  listingId: number | string;
  yachtType?: string | null;
  year?: number | null;
  make?: string | null;
  model?: string | null;
  displayName?: string | null;
  listingReference?: string | null;
}

export function slugifyBoatText(value: string): string {
  return value.normalize('NFKD').replace(/\p{M}/gu, '')
    .toLowerCase().replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Missing data leaves the existing numeric URL in use. Never invent a reference here. */
export function buildDescriptiveBoatPath(input: BoatUrlInput): string | null {
  const id = String(input.listingId);
  if (!/^[1-9]\d*$/.test(id) || !Number.isSafeInteger(Number(id))) return null;
  if (typeof input.listingReference !== 'string' || !/^[1-9]\d{9}$/.test(input.listingReference)) return null;
  const type = slugifyBoatText(input.yachtType || '');
  const name = slugifyBoatText(input.displayName?.trim() || [input.make, input.model].filter(Boolean).join(' '));
  // Existing titles often already contain the model year; preserve that visible wording.
  const titleYear = /^([1-9]\d{3})-./.exec(name)?.[1];
  const resolvedYear = titleYear ? Number(titleYear) : input.year;
  if (!type || !name || !Number.isInteger(resolvedYear) || resolvedYear! < 1000 || resolvedYear! > 9999) return null;
  const year = String(resolvedYear);
  const slug = name === year || name.startsWith(year + '-') ? name : `${year}-${name}`;
  if (slug === year) return null;
  return `/boats/${id}/${type}/${slug}/${input.listingReference}`;
}

/** Only accepts the complete route, never truncated IDs, query strings or arbitrary suffixes. */
export function isDescriptiveBoatPath(path: string): boolean {
  const match = /^\/boats\/([1-9]\d*)\/[a-z0-9]+(?:-[a-z0-9]+)*\/[1-9]\d{3}-[a-z0-9]+(?:-[a-z0-9]+)*\/[1-9]\d{9}$/.exec(path);
  return !!match && Number.isSafeInteger(Number(match[1]));
}

/** API product wrapper, shared by SSR, sitemap and detail metadata. */
export function boatPathFromProduct(product: any): string {
  const boat = product?.boat;
  return buildDescriptiveBoatPath({
    listingId: product?.productId,
    yachtType: boat?.boatType?.boatTypeName,
    year: boat?.boatYear,
    displayName: boat?.boatName,
    listingReference: product?.listingReference,
  }) || `/boats/${product?.productId}`;
}
